import { useEffect, useMemo, useState } from 'react'
import { ActivityIcon, BellIcon, BarChart3Icon, CalculatorIcon, TrendingUpIcon } from 'lucide-react'
import {
  CONDITION_FNS,
  CONDITION_WINDOWS,
  EXPR_ALIAS_RE,
  EXPR_SOURCE_FNS,
  type AggConditionLeaf,
  type AlarmActiveConditionLeaf,
  type ConditionCmp,
  type ConditionFn,
  type ConditionLeaf,
  type ConditionWindow,
  type DeviationConditionLeaf,
  type DeviationMetric,
  type ExprConditionLeaf,
  type ExprConditionSource,
  type ExprSourceFn,
  type LiveConditionLeaf
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { cn } from '../../lib/utils'
import { canAddExprSource, defaultLeaf, nextExprAlias } from './condition-defaults'
import { cmpsForLiveKind, coerceCmpForKind, liveValueKind, parseLiveInput } from './live-value'

const CMP_OPTIONS: { value: ConditionCmp; label: string }[] = [
  { value: 'gt', label: '> greater than' },
  { value: 'gte', label: '≥ greater or equal' },
  { value: 'lt', label: '< less than' },
  { value: 'lte', label: '≤ less or equal' },
  { value: 'eq', label: '= equal' },
  { value: 'ne', label: '≠ not equal' }
]

const METRIC_OPTIONS: { value: DeviationMetric; label: string }[] = [
  { value: 'zscore', label: 'zscore' },
  { value: 'abs_diff', label: 'abs_diff' },
  { value: 'pct_from_baseline', label: 'pct_from_baseline' }
]

const TYPE_OPTIONS: {
  kind: ConditionLeaf['kind']
  label: string
  desc: string
  icon: React.ReactNode
}[] = [
  {
    kind: 'live',
    label: 'Live reading',
    desc: 'Compare the current tag value to a number, string, or boolean.',
    icon: <ActivityIcon className="size-4" />
  },
  {
    kind: 'agg',
    label: 'Aggregate over window',
    desc: 'Reduce a time window (avg, min, max, …) then compare.',
    icon: <BarChart3Icon className="size-4" />
  },
  {
    kind: 'deviation',
    label: 'Deviation from baseline',
    desc: 'z-score, abs diff, or % from a baseline aggregate.',
    icon: <TrendingUpIcon className="size-4" />
  },
  {
    kind: 'alarm_active',
    label: 'Alarm active',
    desc: "Fire when the tag's named alarm is currently active.",
    icon: <BellIcon className="size-4" />
  },
  {
    kind: 'expr',
    label: 'Expression',
    desc: 'Combine named tags with a numeric formula, then compare.',
    icon: <CalculatorIcon className="size-4" />
  }
]

export interface LeafConditionDialogProps {
  open: boolean
  mode: 'add' | 'edit'
  initial?: ConditionLeaf | null
  availableTagPaths: string[]
  onOpenChange: (open: boolean) => void
  onSave: (leaf: ConditionLeaf) => void
}

function withLeafTag(leaf: ConditionLeaf, tagId: string, tagPath?: string): ConditionLeaf {
  if (leaf.kind === 'expr') return leaf
  return { ...leaf, tag_id: tagId, tag_path: tagPath }
}

export function LeafConditionDialog({
  open,
  mode,
  initial,
  availableTagPaths,
  onOpenChange,
  onSave
}: LeafConditionDialogProps) {
  const [kind, setKind] = useState<ConditionLeaf['kind'] | null>(null)
  const [draft, setDraft] = useState<ConditionLeaf>(defaultLeaf('live'))
  const [tagQuery, setTagQuery] = useState('')

  useEffect(() => {
    if (!open) return
    if (mode === 'edit' && initial) {
      setKind(initial.kind)
      setDraft(initial)
    } else {
      setKind(null)
      setDraft(defaultLeaf('live'))
    }
    setTagQuery('')
  }, [open, mode, initial])

  const tagResults = useMemo(() => {
    const q = tagQuery.trim().toLowerCase()
    const fromAvailable = availableTagPaths.filter((t) => !q || t.toLowerCase().includes(q))
    if (q && !availableTagPaths.some((t) => t.toLowerCase() === q)) {
      return [tagQuery.trim(), ...fromAvailable]
    }
    return fromAvailable
  }, [availableTagPaths, tagQuery])

  const canSaveExpr =
    draft.kind === 'expr' &&
    draft.expression.trim().length > 0 &&
    Object.keys(draft.sources).length > 0 &&
    Object.entries(draft.sources).every(
      ([alias, src]) => EXPR_ALIAS_RE.test(alias) && src.tag_id.trim().length > 0
    )
  const canSave =
    !!kind &&
    (kind === 'expr'
      ? canSaveExpr
      : draft.kind !== 'expr' &&
        draft.tag_id.trim().length > 0 &&
        (kind !== 'alarm_active' ||
          (draft as AlarmActiveConditionLeaf).alarm_name.trim().length > 0))

  const pickKind = (next: ConditionLeaf['kind']) => {
    const base = defaultLeaf(next)
    setKind(next)
    if (next === 'expr' || draft.kind === 'expr') {
      setDraft({ ...base, description: draft.description })
      return
    }
    setDraft({
      ...base,
      tag_id: draft.tag_id,
      tag_path: draft.tag_path,
      description: draft.description,
      ...(next !== 'alarm_active' &&
      draft.kind !== 'alarm_active' &&
      'cmp' in draft &&
      'value' in draft
        ? { cmp: draft.cmp, value: draft.value }
        : {})
    } as ConditionLeaf)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[88vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
        <DialogHeader className="border-b border-border px-5 py-4 text-left">
          <p className="text-10 font-semibold uppercase tracking-[0.14em] text-primary">
            Leaf condition
          </p>
          <DialogTitle>{mode === 'edit' ? 'Edit condition' : 'Add condition'}</DialogTitle>
          <DialogDescription className="sr-only">
            Configure a live, aggregate, deviation, alarm-active, or expression leaf.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {!kind ? (
            <div className="flex flex-col gap-2">
              <p className="mb-1 text-11 font-semibold uppercase tracking-wider text-muted-foreground">
                Condition type
              </p>
              {TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.kind}
                  type="button"
                  onClick={() => pickKind(opt.kind)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg border border-border px-3 py-3 text-left',
                    'transition-colors hover:bg-accent/40'
                  )}
                >
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-foreground">
                    {opt.icon}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-foreground">{opt.label}</span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">{opt.desc}</span>
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2.5">
                <span className="text-sm font-medium">
                  {TYPE_OPTIONS.find((o) => o.kind === kind)?.label}
                </span>
                <Button type="button" variant="ghost" size="sm" onClick={() => setKind(null)}>
                  Change type
                </Button>
              </div>

              {kind === 'expr' && draft.kind === 'expr' ? (
                <ExprDialogFields
                  leaf={draft}
                  tagQuery={tagQuery}
                  tagResults={tagResults}
                  onTagQueryChange={setTagQuery}
                  onChange={setDraft}
                />
              ) : (
                <div className="flex flex-col gap-2">
                  <Label className="text-11 font-semibold uppercase tracking-wider text-muted-foreground">
                    Tag
                  </Label>
                  {draft.kind !== 'expr' && (draft.tag_id || draft.tag_path) ? (
                    <div className="flex items-center justify-between gap-2 rounded-lg border border-transparent bg-primary/10 px-3 py-2">
                      <span className="truncate font-mono text-sm">
                        {draft.tag_path || draft.tag_id}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setDraft(withLeafTag(draft, ''))}
                      >
                        Change
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <Input
                        value={tagQuery}
                        onChange={(e) => setTagQuery(e.target.value)}
                        placeholder="Search tags or paste a registry tag id"
                        className="font-mono text-sm"
                      />
                      <div className="max-h-40 overflow-y-auto rounded-lg border border-border">
                        {tagResults.length === 0 ? (
                          <p className="px-3 py-2 text-xs text-muted-foreground">No tags match.</p>
                        ) : (
                          tagResults.map((tp) => (
                            <button
                              key={tp}
                              type="button"
                              onClick={() => {
                                // availableTagPaths may be resolved paths (read view) or
                                // registry ids — store both so save always carries tag_id.
                                setDraft(withLeafTag(draft, tp, tp))
                                setTagQuery('')
                              }}
                              className="flex w-full border-b border-border px-3 py-2 text-left font-mono text-xs last:border-b-0 hover:bg-accent/40"
                            >
                              {tp}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {kind === 'agg' && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Function">
                    <Select
                      value={(draft as AggConditionLeaf).fn}
                      onValueChange={(v) =>
                        v && setDraft({ ...(draft as AggConditionLeaf), fn: v as ConditionFn })
                      }
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONDITION_FNS.map((f) => (
                          <SelectItem key={f} value={f}>
                            {f}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Window">
                    <Select
                      value={(draft as AggConditionLeaf).window}
                      onValueChange={(v) =>
                        v &&
                        setDraft({
                          ...(draft as AggConditionLeaf),
                          window: v as ConditionWindow
                        })
                      }
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONDITION_WINDOWS.map((w) => (
                          <SelectItem key={w} value={w}>
                            {w}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              )}

              {kind === 'deviation' && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Baseline fn">
                    <Select
                      value={(draft as DeviationConditionLeaf).baseline_fn ?? 'avg'}
                      onValueChange={(v) =>
                        v &&
                        setDraft({
                          ...(draft as DeviationConditionLeaf),
                          baseline_fn: v as ConditionFn
                        })
                      }
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONDITION_FNS.map((f) => (
                          <SelectItem key={f} value={f}>
                            {f}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Baseline window">
                    <Select
                      value={(draft as DeviationConditionLeaf).window}
                      onValueChange={(v) =>
                        v &&
                        setDraft({
                          ...(draft as DeviationConditionLeaf),
                          window: v as ConditionWindow
                        })
                      }
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONDITION_WINDOWS.map((w) => (
                          <SelectItem key={w} value={w}>
                            {w}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Metric" className="col-span-2">
                    <Select
                      value={(draft as DeviationConditionLeaf).metric ?? 'zscore'}
                      onValueChange={(v) =>
                        v &&
                        setDraft({
                          ...(draft as DeviationConditionLeaf),
                          metric: v as DeviationMetric
                        })
                      }
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {METRIC_OPTIONS.map((m) => (
                          <SelectItem key={m.value} value={m.value}>
                            {m.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              )}

              {kind === 'alarm_active' ? (
                <Field label="Alarm name">
                  <Input
                    value={(draft as AlarmActiveConditionLeaf).alarm_name}
                    onChange={(e) =>
                      setDraft({
                        ...(draft as AlarmActiveConditionLeaf),
                        alarm_name: e.target.value
                      })
                    }
                    placeholder="High Pressure"
                  />
                </Field>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Comparator">
                    <Select
                      value={(draft as LiveConditionLeaf).cmp}
                      onValueChange={(v) =>
                        v && setDraft({ ...draft, cmp: v as ConditionCmp } as ConditionLeaf)
                      }
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(kind === 'live'
                          ? CMP_OPTIONS.filter((c) =>
                              cmpsForLiveKind(
                                liveValueKind(undefined, (draft as LiveConditionLeaf).value)
                              ).includes(c.value)
                            )
                          : CMP_OPTIONS
                        ).map((c) => (
                          <SelectItem key={c.value} value={c.value}>
                            {c.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Value">
                    {kind === 'live' &&
                    liveValueKind(undefined, (draft as LiveConditionLeaf).value) === 'boolean' ? (
                      <Select
                        value={String((draft as LiveConditionLeaf).value)}
                        onValueChange={(v) =>
                          v &&
                          setDraft({
                            ...(draft as LiveConditionLeaf),
                            value: v === 'true'
                          })
                        }
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="true">true</SelectItem>
                          <SelectItem value="false">false</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        type={
                          kind === 'live' &&
                          liveValueKind(undefined, (draft as LiveConditionLeaf).value) === 'string'
                            ? 'text'
                            : 'number'
                        }
                        step="any"
                        value={String((draft as LiveConditionLeaf).value)}
                        onChange={(e) => {
                          if (kind !== 'live') {
                            setDraft({
                              ...draft,
                              value: Number(e.target.value)
                            } as ConditionLeaf)
                            return
                          }
                          const currentKind = liveValueKind(
                            undefined,
                            (draft as LiveConditionLeaf).value
                          )
                          const value =
                            currentKind === 'string'
                              ? e.target.value
                              : parseLiveInput(e.target.value)
                          const valueKind = liveValueKind(undefined, value)
                          setDraft({
                            ...(draft as LiveConditionLeaf),
                            value,
                            cmp: coerceCmpForKind((draft as LiveConditionLeaf).cmp, valueKind)
                          })
                        }}
                      />
                    )}
                  </Field>
                </div>
              )}

              <Field label="Description · auto-generated if blank">
                <Input
                  value={draft.description ?? ''}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      description: e.target.value || undefined
                    })
                  }
                  placeholder="Operator-facing note"
                />
              </Field>
            </div>
          )}
        </div>

        {kind && (
          <DialogFooter className="border-t border-border px-5 py-3">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!canSave}
              onClick={() => {
                if (!canSave) return
                onSave(draft)
                onOpenChange(false)
              }}
            >
              {mode === 'edit' ? 'Save condition' : 'Add condition'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}

function ExprDialogFields({
  leaf,
  tagQuery,
  tagResults,
  onTagQueryChange,
  onChange
}: {
  leaf: ExprConditionLeaf
  tagQuery: string
  tagResults: string[]
  onTagQueryChange: (q: string) => void
  onChange: (next: ConditionLeaf) => void
}) {
  const aliases = Object.keys(leaf.sources)
  const updateSource = (alias: string, patch: Partial<ExprConditionSource>) => {
    onChange({
      ...leaf,
      sources: { ...leaf.sources, [alias]: { ...leaf.sources[alias], ...patch } }
    })
  }
  const renameAlias = (from: string, to: string) => {
    const next = to.trim()
    if (!next || next === from || leaf.sources[next]) return
    const sources: Record<string, ExprConditionSource> = {}
    for (const [alias, src] of Object.entries(leaf.sources)) {
      sources[alias === from ? next : alias] = src
    }
    onChange({ ...leaf, sources })
  }

  return (
    <div className="flex flex-col gap-3">
      <Field label="Expression">
        <Input
          value={leaf.expression}
          onChange={(e) => onChange({ ...leaf, expression: e.target.value })}
          placeholder="a + b"
          className="font-mono text-sm"
        />
      </Field>
      <div className="flex flex-col gap-2">
        <span className="text-11 font-semibold uppercase tracking-wider text-muted-foreground">
          Sources
        </span>
        {aliases.map((alias) => {
          const src = leaf.sources[alias]
          const fn: ExprSourceFn = src.fn ?? 'last'
          return (
            <div key={alias} className="flex flex-col gap-2 rounded-lg border border-border p-3">
              <div className="grid grid-cols-[5.5rem_1fr_auto] items-end gap-2">
                <Field label="Alias">
                  <Input
                    value={alias}
                    onChange={(e) => renameAlias(alias, e.target.value)}
                    className="font-mono text-sm"
                  />
                </Field>
                <Field label="Tag id">
                  <Input
                    value={src.tag_id}
                    onChange={(e) => updateSource(alias, { tag_id: e.target.value })}
                    placeholder="Registry tag UUID"
                    className="font-mono text-sm"
                  />
                </Field>
                {aliases.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const { [alias]: _, ...rest } = leaf.sources
                      onChange({ ...leaf, sources: rest })
                    }}
                  >
                    Remove
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Reduce as">
                  <Select
                    value={fn}
                    onValueChange={(v) => {
                      if (!v) return
                      const nextFn = v as ExprSourceFn
                      updateSource(alias, {
                        fn: nextFn === 'last' ? undefined : nextFn,
                        window: nextFn === 'last' ? undefined : (src.window ?? '5m')
                      })
                    }}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPR_SOURCE_FNS.map((f) => (
                        <SelectItem key={f} value={f}>
                          {f}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                {fn !== 'last' && (
                  <Field label="Window">
                    <Select
                      value={(src.window as ConditionWindow | undefined) ?? '5m'}
                      onValueChange={(v) =>
                        v && updateSource(alias, { window: v as ConditionWindow })
                      }
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONDITION_WINDOWS.map((w) => (
                          <SelectItem key={w} value={w}>
                            {w}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                )}
              </div>
            </div>
          )
        })}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          disabled={!canAddExprSource(leaf.sources)}
          onClick={() => {
            const alias = nextExprAlias(aliases)
            onChange({
              ...leaf,
              sources: { ...leaf.sources, [alias]: { tag_id: '' } }
            })
          }}
        >
          Add source
        </Button>
        <p className="m-0 text-xs text-muted-foreground">
          Paste a registry UUID, or pick from the list after searching.
        </p>
        <Input
          value={tagQuery}
          onChange={(e) => onTagQueryChange(e.target.value)}
          placeholder="Search tags to copy an id"
          className="font-mono text-sm"
        />
        {tagQuery.trim() !== '' && (
          <div className="max-h-32 overflow-y-auto rounded-lg border border-border">
            {tagResults.length === 0 ? (
              <p className="px-3 py-2 text-xs text-muted-foreground">No tags match.</p>
            ) : (
              tagResults.map((tp) => (
                <button
                  key={tp}
                  type="button"
                  onClick={() => {
                    const target = aliases.find((a) => !leaf.sources[a].tag_id.trim()) ?? aliases[0]
                    if (!target) return
                    updateSource(target, { tag_id: tp, tag_path: tp })
                    onTagQueryChange('')
                  }}
                  className="flex w-full border-b border-border px-3 py-2 text-left font-mono text-xs last:border-b-0 hover:bg-accent/40"
                >
                  {tp}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function Field({
  label,
  className,
  children
}: {
  label: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <label className={cn('flex flex-col gap-1.5', className)}>
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
    </label>
  )
}
